import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import client from '../../api/client';
import Layout from '../../components/Layout';
import './Settings.css';
import './Kiosk.css';

// Backend stores timeout in SECONDS (it is also the kiosk token lifetime),
// so a value of 0 would mean instantly-expired sessions. Options are minutes
// in the UI, converted on save.
const AUTO_LOGOUT_OPTIONS = [
  { label: '2 minutes',  value: 2 },
  { label: '5 minutes',  value: 5 },
  { label: '10 minutes', value: 10 },
  { label: '15 minutes', value: 15 },
  { label: '30 minutes', value: 30 },
  { label: '1 hour',     value: 60 },
  { label: '8 hours',    value: 480 },
];

const PIN_LENGTH_OPTIONS = [4, 5, 6, 7, 8];

export default function Kiosk() {
  const { org } = useAuth();

  const [operators, setOperators]   = useState([]);
  const [loading, setLoading]       = useState(true);

  // Settings (hydrated from GET /kiosk/settings)
  const [enabled, setEnabled]         = useState(true);
  const [autoLogout, setAutoLogout]   = useState(15);
  const [pinLength, setPinLength]     = useState(4);
  const [welcomeMsg, setWelcomeMsg]   = useState('');
  const [allowMove, setAllowMove]     = useState(true);
  const [allowStatus, setAllowStatus] = useState(true);
  const [allowCount, setAllowCount]   = useState(false);

  // Add-operator modal
  const [showModal, setShowModal] = useState(false);
  const [form, setForm]           = useState({ name: '', pin: '' });
  const [saving, setSaving]       = useState(false);

  // Set/reset PIN modal for an existing operator
  const [pinTarget, setPinTarget] = useState(null); // operator row
  const [newPin, setNewPin]       = useState('');

  const [error, setError]                   = useState('');
  const [success, setSuccess]               = useState('');
  const [savingSettings, setSavingSettings] = useState(false);

  const planLimits = { starter: 20, pro: 250, premium: Infinity };
  const maxPins    = planLimits[org?.plan] || 20;
  const kioskUrl   = org?.slug
    ? `${window.location.protocol}//${window.location.host}/kiosk/${org.slug}`
    : '';

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [settingsRes, opsRes] = await Promise.all([
        client.get('/kiosk/settings'),
        client.get('/kiosk/operators'),
      ]);

      const s = settingsRes.data;
      setEnabled(s.enabled ?? true);
      setAutoLogout(Math.max(1, Math.round((s.timeout_sec ?? 900) / 60)));
      setPinLength(s.pin_length ?? 4);
      setWelcomeMsg(s.welcome_message ?? '');
      setAllowMove(s.allow_move_jobs ?? true);
      setAllowStatus(s.allow_update_status ?? true);
      setAllowCount(s.allow_count_inventory ?? false);

      // Kiosk users = anyone with a PIN set (active or disabled).
      setOperators(opsRes.data.filter((u) => u.pin_status !== 'none'));
    } catch (err) {
      setError('Failed to load kiosk settings');
    } finally {
      setLoading(false);
    }
  };

  const flash = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 3000);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(kioskUrl);
    flash('Link copied to clipboard');
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    setError('');
    try {
      await client.put('/kiosk/settings', {
        enabled,
        pin_length: pinLength,
        timeout_sec: autoLogout * 60,
        welcome_message: welcomeMsg,
        allow_move_jobs: allowMove,
        allow_update_status: allowStatus,
        allow_count_inventory: allowCount,
      });
      flash('Kiosk settings saved');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const generatePin = (setter) => {
    let pin = '';
    for (let i = 0; i < pinLength; i++) pin += Math.floor(Math.random() * 10);
    setter(pin);
  };

  // Create operator: account first, then name + PIN via kiosk routes.
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.pin.length !== pinLength) {
      setError(`PIN must be exactly ${pinLength} digits`);
      return;
    }
    setSaving(true);
    try {
      const email = `${form.name.toLowerCase().replace(/\s+/g, '.')}.kiosk@${org?.slug}.trace`;
      // Strong random password -- operators only ever log in by PIN.
      const password = `k_${crypto.randomUUID()}`;

      const created = await client.post('/org/users', {
        email,
        password,
        role: 'operator',
      });
      const userId = created.data.id;

      await client.put(`/kiosk/operators/${userId}/name`, { name: form.name });
      await client.put(`/kiosk/operators/${userId}/pin`, { pin: form.pin });

      setShowModal(false);
      setForm({ name: '', pin: '' });
      flash('Kiosk operator added');
      loadAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add operator');
    } finally {
      setSaving(false);
    }
  };

  const submitNewPin = async (e) => {
    e.preventDefault();
    setError('');
    if (newPin.length !== pinLength) {
      setError(`PIN must be exactly ${pinLength} digits`);
      return;
    }
    try {
      await client.put(`/kiosk/operators/${pinTarget.id}/pin`, { pin: newPin });
      setPinTarget(null);
      setNewPin('');
      flash('PIN updated');
      loadAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update PIN');
    }
  };

  const togglePin = async (u) => {
    try {
      if (u.pin_status === 'active') {
        await client.put(`/kiosk/operators/${u.id}/pin/deactivate`);
        flash('PIN disabled');
      } else {
        // Re-enabling requires setting a fresh PIN.
        setPinTarget(u);
        setNewPin('');
        return;
      }
      loadAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update PIN');
    }
  };

  const removeUser = async (id) => {
    if (!window.confirm('Remove this kiosk operator? They will no longer be able to sign in.')) return;
    try {
      await client.put(`/org/users/${id}`, { is_active: false });
      await client.put(`/kiosk/operators/${id}/pin/deactivate`);
      flash('Operator removed');
      loadAll();
    } catch (err) {
      setError('Failed to remove operator');
    }
  };

  if (loading) return <Layout><div style={{color:'#6b7280',padding:'20px'}}>Loading...</div></Layout>;

  const pinPct = maxPins === Infinity ? 0 : Math.min((operators.length / maxPins) * 100, 100);

  return (
    <Layout>
      <div className="spage-header">
        <h1>Kiosk Settings</h1>
      </div>

      {success && <div className="spage-success">{success}</div>}
      {error   && <div className="spage-error">{error}</div>}

      {/* Kiosk URL */}
      <div className="spage-section">
        <div className="spage-section-title">Kiosk URL</div>
        <p className="spage-hint" style={{marginBottom:'14px'}}>
          Open this link on any floor tablet to launch kiosk mode. Operators sign in with their PIN.
        </p>
        <div className="kiosk-url-row">
          <div className="kiosk-url-text">{kioskUrl || 'Set up your org slug to generate a link'}</div>
          <button className="spage-btn-save" style={{marginTop:0}} onClick={copyLink}>Copy Link</button>
        </div>
      </div>

      {/* Configuration */}
      <div className="spage-section">
        <div className="spage-section-title">Configuration</div>

        <div className="spage-field">
          <label>Kiosk Mode</label>
          <select value={enabled ? '1' : '0'} onChange={e => setEnabled(e.target.value === '1')}>
            <option value="1">Enabled</option>
            <option value="0">Disabled</option>
          </select>
          <div className="spage-hint">When disabled, the kiosk link shows a "kiosk is off" screen and PINs stop working.</div>
        </div>

        <div className="spage-field">
          <label>Auto Logout</label>
          <select value={autoLogout} onChange={e => setAutoLogout(parseInt(e.target.value))}>
            {AUTO_LOGOUT_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <div className="spage-hint">Operator sessions end automatically after this long.</div>
        </div>

        <div className="spage-field">
          <label>PIN Length</label>
          <select value={pinLength} onChange={e => setPinLength(parseInt(e.target.value))}>
            {PIN_LENGTH_OPTIONS.map(n => (
              <option key={n} value={n}>{n} digits</option>
            ))}
          </select>
          <div className="spage-hint">How many digits operator PINs must be. Applies to new PINs only.</div>
        </div>

        <div className="spage-field">
          <label>Welcome Message</label>
          <input
            value={welcomeMsg}
            onChange={e => setWelcomeMsg(e.target.value)}
            placeholder="Welcome. Enter your PIN to get started."
            style={{maxWidth:'100%'}}
          />
          <div className="spage-hint">This message appears on the kiosk screen before operators sign in.</div>
        </div>

        <div className="spage-field">
          <label>Operator Permissions</label>
          <select value={allowStatus ? '1' : '0'} onChange={e => setAllowStatus(e.target.value === '1')}>
            <option value="1">Can update job status</option>
            <option value="0">Cannot update job status</option>
          </select>
          <div style={{height:'8px'}} />
          <select value={allowMove ? '1' : '0'} onChange={e => setAllowMove(e.target.value === '1')}>
            <option value="1">Can move jobs between locations</option>
            <option value="0">Cannot move jobs</option>
          </select>
          <div style={{height:'8px'}} />
          <select value={allowCount ? '1' : '0'} onChange={e => setAllowCount(e.target.value === '1')}>
            <option value="1">Can count inventory</option>
            <option value="0">Cannot count inventory</option>
          </select>
          <div className="spage-hint">Controls what operators can do from the kiosk.</div>
        </div>

        {/* Preview */}
        <div className="kiosk-preview">
          <div className="kiosk-preview-label">Preview</div>
          <div className="kiosk-preview-screen">
            <div className="kiosk-preview-logo">TRACE</div>
            <div className="kiosk-preview-msg">{welcomeMsg || 'Welcome. Enter your PIN to get started.'}</div>
            <div className="kiosk-preview-dots">
              {Array.from({length: pinLength}).map((_, i) => (
                <div key={i} className="kiosk-preview-dot" />
              ))}
            </div>
          </div>
        </div>

        <button className="spage-btn-save" onClick={saveSettings} disabled={savingSettings}>
          {savingSettings ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* Operators */}
      <div className="spage-section">
        <div className="kiosk-users-header">
          <div>
            <div className="spage-section-title" style={{marginBottom:'4px'}}>Kiosk Operators</div>
            <div className="spage-hint">
              {operators.length} of {maxPins === Infinity ? 'unlimited' : maxPins} PIN slots used
            </div>
          </div>
          <button
            className="spage-btn-save"
            style={{marginTop:0}}
            onClick={() => { setForm({ name: '', pin: '' }); setError(''); setShowModal(true); }}
            disabled={operators.length >= maxPins}
          >
            + Add Operator
          </button>
        </div>

        {maxPins !== Infinity && (
          <div className="kiosk-pin-track">
            <div className="kiosk-pin-fill" style={{width:`${pinPct}%`}} />
          </div>
        )}

        {operators.length === 0 ? (
          <div className="spage-hint" style={{padding:'20px 0'}}>
            No kiosk operators yet. Add your first one.
          </div>
        ) : (
          <div className="kiosk-user-list">
            {operators.map(u => (
              <div key={u.id} className="kiosk-user-row">
                <div className="kiosk-user-left">
                  <div className="kiosk-avatar">
                    {(u.name || u.email)[0].toUpperCase()}
                  </div>
                  <div>
                    <div className="kiosk-user-name">{u.name || u.email}</div>
                    <div className="kiosk-user-pin">
                      {u.pin_status === 'active' ? 'PIN set' : 'PIN disabled'}
                    </div>
                  </div>
                </div>
                <div className="kiosk-user-right">
                  <span className={`kiosk-badge ${u.pin_status === 'active' && u.is_active ? 'active' : 'inactive'}`}>
                    {u.pin_status === 'active' && u.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <button
                    className="kiosk-remove-btn"
                    style={{marginRight:'6px'}}
                    onClick={() => { setPinTarget(u); setNewPin(''); setError(''); }}
                  >
                    Reset PIN
                  </button>
                  <button
                    className="kiosk-remove-btn"
                    style={{marginRight:'6px'}}
                    onClick={() => togglePin(u)}
                  >
                    {u.pin_status === 'active' ? 'Disable PIN' : 'Enable PIN'}
                  </button>
                  <button className="kiosk-remove-btn" onClick={() => removeUser(u.id)}>Remove</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add operator modal */}
      {showModal && (
        <div className="kiosk-overlay" onClick={() => setShowModal(false)}>
          <div className="kiosk-modal" onClick={e => e.stopPropagation()}>
            <div className="kiosk-modal-header">
              <h2>Add Kiosk Operator</h2>
              <button className="kiosk-modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            {error && <div className="spage-error">{error}</div>}
            <form onSubmit={handleSubmit}>
              <div className="spage-field">
                <label>Operator Name</label>
                <input
                  value={form.name}
                  onChange={e => setForm({...form, name: e.target.value})}
                  placeholder="e.g. John Smith"
                  required
                />
              </div>
              <div className="spage-field">
                <label>PIN ({pinLength} digits)</label>
                <div className="kiosk-pin-row">
                  <input
                    value={form.pin}
                    onChange={e => setForm({...form, pin: e.target.value.replace(/\D/g,'').slice(0, pinLength)})}
                    placeholder={'0'.repeat(pinLength)}
                    required
                    style={{maxWidth:'160px', letterSpacing:'0.2em', fontSize:'16px'}}
                  />
                  <button type="button" className="spage-btn-upgrade" onClick={() => generatePin((pin) => setForm(f => ({...f, pin})))}>
                    Generate
                  </button>
                </div>
                <div className="spage-hint">
                  Must be exactly {pinLength} digits. Write it down -- PINs are stored securely and can't be viewed later, only reset.
                </div>
              </div>
              <div className="kiosk-modal-actions">
                <button type="button" className="spage-btn-upgrade" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="spage-btn-save" style={{marginTop:0}} disabled={saving}>
                  {saving ? 'Adding...' : 'Add Operator'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Set / reset PIN modal */}
      {pinTarget && (
        <div className="kiosk-overlay" onClick={() => setPinTarget(null)}>
          <div className="kiosk-modal" onClick={e => e.stopPropagation()}>
            <div className="kiosk-modal-header">
              <h2>Set PIN — {pinTarget.name || pinTarget.email}</h2>
              <button className="kiosk-modal-close" onClick={() => setPinTarget(null)}>✕</button>
            </div>
            {error && <div className="spage-error">{error}</div>}
            <form onSubmit={submitNewPin}>
              <div className="spage-field">
                <label>New PIN ({pinLength} digits)</label>
                <div className="kiosk-pin-row">
                  <input
                    value={newPin}
                    onChange={e => setNewPin(e.target.value.replace(/\D/g,'').slice(0, pinLength))}
                    placeholder={'0'.repeat(pinLength)}
                    required
                    style={{maxWidth:'160px', letterSpacing:'0.2em', fontSize:'16px'}}
                  />
                  <button type="button" className="spage-btn-upgrade" onClick={() => generatePin(setNewPin)}>
                    Generate
                  </button>
                </div>
              </div>
              <div className="kiosk-modal-actions">
                <button type="button" className="spage-btn-upgrade" onClick={() => setPinTarget(null)}>Cancel</button>
                <button type="submit" className="spage-btn-save" style={{marginTop:0}}>
                  Save PIN
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}